import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import Layout from './components/Layout';
import PrivateRoute from './components/PrivateRoute';
import ScrollToTop from './components/ScrollToTop';

// 同步加载的页面（首屏关键路径）
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';

// 懒加载的页面
const AboutPage = lazy(() => import('./pages/AboutPage'));
const Home = lazy(() => import('./pages/Home'));
const Community = lazy(() => import('./pages/Community'));
const GroupDetail = lazy(() => import('./pages/GroupDetail'));
const Tools = lazy(() => import('./pages/Tools'));
const ToolDetail = lazy(() => import('./pages/ToolDetail'));
const Courses = lazy(() => import('./pages/Courses'));
const VideoDetail = lazy(() => import('./pages/VideoDetail'));
const CourseDetail = lazy(() => import('./pages/CourseDetail'));
const Assessments = lazy(() => import('./pages/Assessments'));
const AssessmentDetail = lazy(() => import('./pages/AssessmentDetail'));
const AssessmentResult = lazy(() => import('./pages/AssessmentResult'));
const QnA = lazy(() => import('./pages/QnA'));
const Guestbook = lazy(() => import('./pages/Guestbook'));
const MoodTracker = lazy(() => import('./pages/MoodTracker'));
const DBTDiaryCard = lazy(() => import('./pages/DBTDiaryCard'));
const Knowledge = lazy(() => import('./pages/Knowledge'));
const KnowledgeDetail = lazy(() => import('./pages/KnowledgeDetail'));
const Profile = lazy(() => import('./pages/Profile'));
const DBTLanding = lazy(() => import('./components/DBTLanding'));
const DBTSkillDetail = lazy(() => import('./pages/DBTSkillDetail'));
const RODBTLanding = lazy(() => import('./components/RODBTLanding'));
const RODBTSkillDetail = lazy(() => import('./pages/RODBTSkillDetail')); 
const EMDRAccessories = lazy(() => import('./pages/EMDRAccessories'));

// 加载占位符
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-cream-50">
    <div className="flex flex-col items-center space-y-4">
      <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-500 rounded-full animate-spin" />
      <p className="text-slate-500 text-sm">加载中...</p>
    </div>
  </div>
);

// 公开布局（无需登录）
const PublicLayout = () => (
  <Layout>
    <Outlet />
  </Layout>
);

// 需要登录的布局
const AuthenticatedLayout = () => (
  <PrivateRoute>
    <Layout>
      <Outlet />
    </Layout>
  </PrivateRoute>
);

function App() {
  return (
    <AuthProvider>
      <Router future={{ v7_relativeSplatPath: true }}>
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* 入口页面 */}
            <Route path="/" element={<LandingPage />} />

            {/* 保留登录注册页面（后台管理需要） */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            
            {/* ========== 主要功能路由（公开访问，无需登录） ========== */}
            <Route element={<PublicLayout />}>
              <Route path="/home" element={<Home />} />
              
              {/* 社区 */}
              <Route path="/community" element={<Community />} />
              <Route path="/group/:id" element={<GroupDetail />} />
              
              {/* 课程 */}
              <Route path="/courses" element={<Courses />} />
              <Route path="/courses/:id" element={<CourseDetail />} />
              <Route path="/videos/:id" element={<VideoDetail />} />
              
              {/* 问答 */}
              <Route path="/qa" element={<QnA />} />
              
              {/* 留言墙 */}
              <Route path="/guestbook" element={<Guestbook />} />
              
              {/* 科普知识库 */}
              <Route path="/knowledge" element={<Knowledge />} />
              <Route path="/knowledge/:id" element={<KnowledgeDetail />} />
              
              {/* DBT 技能 */}
              <Route path="/dbt-skills" element={<DBTLanding />} />
              <Route path="/dbt-skills/:skillId" element={<DBTSkillDetail />} />
              
              {/* RO DBT 技能 */}
              <Route path="/ro-dbt-skills" element={<RODBTLanding />} />
              <Route path="/ro-dbt-skills/:skillId" element={<RODBTSkillDetail />} />
              
              {/* 关于我们 */}
              <Route path="/about" element={<AboutPage />} />
              
              {/* EMDR 配件选择 */}
              <Route path="/emdr-accessories" element={<EMDRAccessories />} />
            </Route>
            
            {/* ========== 需要登录的页面 ========== */}
            <Route element={<AuthenticatedLayout />}>
              <Route path="/profile" element={<Profile />} />
              
              {/* 心理测评 */}
              <Route path="/assessments" element={<Assessments />} />
              <Route path="/assessments/:id" element={<AssessmentDetail />} />
              <Route path="/assessments/:id/result" element={<AssessmentResult />} />
              
              {/* 心情记录 */}
              <Route path="/mood-tracker" element={<MoodTracker />} />
              
              {/* DBT 日记卡 */}
              <Route path="/dbt-diary-card" element={<DBTDiaryCard />} />
              
              {/* CBT 工具 */}
              <Route path="/tools" element={<Tools />} />
              <Route path="/tools/:id" element={<ToolDetail />} />
            </Route>
            
            {/* 默认重定向 */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </Router>
    </AuthProvider>
  );
}

export default App;
